import * as BBApi from '.';

const rootUrl: string = BBApi.host + '/users';

export async function query(
	values: any
): Promise<{ users: Record<string, any>[]; totalMatches: number }> {
	const url: URL = new URL(rootUrl);

	url.searchParams.append('offset', String(0));
	url.searchParams.append('amount', String(values.amount));
	if (values.country !== 'all') url.searchParams.append('country', values.country);

	if (Boolean(values.ageFrom)) url.searchParams.append('ageFrom', values.ageFrom);
	if (Boolean(values.ageUntil)) url.searchParams.append('ageUntil', values.ageUntil);

	if (values?.userId !== undefined && values.userId !== '') {
		url.searchParams.append('userId', values.userId);
	}

	const [dateFrom, dateTo] = values?.date ?? [null, null];
	if (dateFrom !== null) url.searchParams.append('dateFrom', dateFrom.format('YYYY-MM-DD'));
	if (dateTo !== null) url.searchParams.append('dateUntil', dateTo.format('YYYY-MM-DD'));
	if (values?.subscription) url.searchParams.append('subscription', values.subscription);

	if (values?.filter1?.target !== undefined && values?.filter1?.search !== undefined) {
		if (values.filter1.target !== 'all') {
			url.searchParams.append('filter1', values.filter1.target);
		}

		if (values.filter1.search !== '') {
			url.searchParams.append('filterSearch1', `%${values.filter1.search}%`);
		}
	}

	if (values?.filter2?.target !== undefined && values?.filter2?.search !== undefined) {
		if (values.filter2.target !== 'all') {
			url.searchParams.append('filter2', values.filter2.target);
		}

		if (values.filter2.search !== '') {
			url.searchParams.append('filterSearch2', `%${values.filter2.search}%`);
		}
	}

	const response: Response = await BBApi.get(url.href);
	const { users, totalMatches }: { users: Record<string, any>[]; totalMatches: number } =
		await response.json();
	return { users, totalMatches };
}

export async function get(id: number): Promise<any> {
	const response: Response = await BBApi.get(rootUrl + `/${id}`);
	const json: any = await response.json();
	return json;
}

export async function create(user: Record<string, any>): Promise<any> {
	const host: string = String(process.env.REACT_APP_BOBLBERG_API_HOST);

	const response: Response = await fetch(host + 'users/signup', {
		body: JSON.stringify({ origin: 'Admin', password: '1234', ...user }),
		headers: { 'Content-Type': 'application/json' },
		method: 'POST',
	});

	if (response.ok === true) {
		const { user: createResponse } = await response.json();
		await BBApi.post(rootUrl + `/finalize`, {
			id: createResponse.id,
			universeId: user.universeId,
		});
		return;
	}

	const json = await response.json();
	if (json?.error) {
		throw new Error(json.error);
	}

	throw new Error('User not created');
}

export async function validateEmail(email: string): Promise<boolean> {
	const response: Response = await BBApi.post(rootUrl + `/validate-email`, { email });
	const { validation } = await response.json();
	return validation;
}

export async function update(id: number, user: Record<string, any>): Promise<void> {
	user.banned = Number(user.banned);
	const response: Response = await BBApi.put(rootUrl + `/${id}`, user);

	if (response.ok === false) {
		throw new Error('User not updated');
	}
}

export async function remove(id: number): Promise<any[] | null> {
	const response: Response = await BBApi.del(rootUrl + `/${id}`);

	if (response.statusText === 'Bad Gateway') {
		const rejectedDeletions: any[] = await response.json();
		return rejectedDeletions;
	}

	return null;
}

export async function getConversations(userId: number): Promise<Record<string, any>[]> {
	const response: Response = await BBApi.get(rootUrl + `/${userId}/conversations`);

	if (response.ok === false) {
		throw new Error('Conversations not found');
	}

	const conversations: Record<string, any>[] = await response.json();
	return conversations;
}

export async function getPosts(userId: number): Promise<Record<string, any>[]> {
	const response: Response = await BBApi.get(rootUrl + `/${userId}/posts`);

	if (response.ok === false) {
		throw new Error('Posts not found');
	}

	const posts: Record<string, any>[] = await response.json();
	return posts;
}

export async function getProfileImages(): Promise<Record<string, any>[]> {
	const response: Response = await BBApi.get(rootUrl + `/profile-images`);

	if (response.ok === false) {
		throw new Error('Messages not found');
	}

	const profileImages: Record<string, any>[] = await response.json();
	return profileImages;
}

export async function updateProfileImageApproval(
	imageId: number,
	approved: boolean
): Promise<void> {
	const response: Response = await BBApi.put(rootUrl + `/profile-images/${imageId}/approval`, {
		approved,
	});

	if (response.ok === false) {
		throw new Error('Profile image not updated');
	}
}

export async function getProfileImage(userId: number) {
	const response: Response = await BBApi.get(rootUrl + `/${userId}/image`);

	if (response.ok === false) {
		return null;
	}

	const profileImage = await response.text();
	return profileImage;
}

export async function updateProfileImage(
	userId: number,
	base64: string | null,
	approved: boolean
): Promise<void> {
	if (base64 !== null) {
		base64 = base64.replace('data:image/jpeg;base64,', '');
	}

	const response: Response = await BBApi.put(rootUrl + `/${userId}/image`, { base64, approved });

	if (response.ok === false) {
		throw new Error('Profile image not updated');
	}
}

export async function sendWarning(username: string, email: string, type: string) {
	await BBApi.post(rootUrl + '/send-warning', { username, email, type });
}
